import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { Modal } from '@/components/Modal';
import { TextField } from '@/components/TextField';
import { Chip } from '@/components';
import { AdminSettingsService } from '@/services/engine/admin-settings-service';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { useAuthUser } from '@/hooks/useAuthUser';
import { ORG_OWNER_ROLE } from '@/hooks/usePermissions';
import { usePasswordComplexityPolicy } from '../hooks/usePasswordComplexityPolicy';
import { PasswordRequirements } from './PasswordRequirements';

interface AddMemberFormValues {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    password: string;
    confirmPassword: string;
    isOrgOwner: boolean;
    isOrgUserManager: boolean;
}

const DEFAULT_VALUES: AddMemberFormValues = {
    email: '',
    firstName: '',
    lastName: '',
    role: 'member',
    password: '',
    confirmPassword: '',
    isOrgOwner: false,
    isOrgUserManager: false,
};

interface AddMemberFormProps {
    isOpen: boolean;
    onClose: () => void;
    onAdded: () => void;
}

export function AddMemberForm({
    isOpen,
    onClose,
    onAdded,
}: AddMemberFormProps) {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const { policy } = usePasswordComplexityPolicy();
    const { user: currentUser } = useAuthUser();
    const canManageOrgRoles =
        currentUser?.roles.includes(ORG_OWNER_ROLE) === true;

    const {
        register,
        watch,
        setValue,
        reset,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<AddMemberFormValues>({
        mode: 'onChange',
        defaultValues: DEFAULT_VALUES,
    });

    const role = watch('role');
    const password = watch('password');
    const isOrgOwner = watch('isOrgOwner');
    const isOrgUserManager = watch('isOrgUserManager');

    const handleClose = () => {
        reset(DEFAULT_VALUES);
        onClose();
    };

    const { isSubmitting, run } = useAsyncAction(
        async (values: AddMemberFormValues) => {
            await AdminSettingsService.addMember({
                email: values.email.trim(),
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                role: values.role,
                password: values.password,
                isOrgOwner: values.isOrgOwner,
                isOrgUserManager: values.isOrgUserManager,
            });
            reset(DEFAULT_VALUES);
            onAdded();
        },
        {
            successMessage: t('members.addSuccess'),
            errorMessage: t('members.addFailed'),
        },
    );

    const onSubmit = handleSubmit((values) => run(values));

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t('members.addTitle')}
            onCancel={handleClose}
            onSubmit={onSubmit}
            submitText={t('members.addSubmit')}
            submitDisabled={!isValid || isSubmitting}
            submitInProgress={isSubmitting}
            size="small"
        >
            <div className="flex flex-col gap-4 px-6 py-6">
                <TextField
                    label={t('members.email')}
                    labelClassName="text-sm-medium text-white-100"
                    type="email"
                    required
                    placeholder="user@example.com"
                    error={errors.email ? t('members.invalidEmail') : undefined}
                    {...register('email', { required: true })}
                />

                <div className="grid grid-cols-2 gap-3">
                    <TextField
                        label={t('members.firstName')}
                        labelClassName="text-sm-medium text-white-100"
                        required
                        placeholder="John"
                        error={
                            errors.firstName
                                ? t('members.invalidFirstName')
                                : undefined
                        }
                        {...register('firstName', {
                            validate: (value) => value.trim().length > 0,
                        })}
                    />
                    <TextField
                        label={t('members.lastName')}
                        labelClassName="text-sm-medium text-white-100"
                        required
                        placeholder="Doe"
                        error={
                            errors.lastName
                                ? t('members.invalidLastName')
                                : undefined
                        }
                        {...register('lastName', {
                            validate: (value) => value.trim().length > 0,
                        })}
                    />
                </div>

                <div className="flex flex-col gap-1.5">
                    <label className="text-sm-medium text-white-100">
                        {t('members.role')}
                    </label>
                    <div className="flex gap-2">
                        {(['member', 'admin', 'reader'] as const).map(
                            (roleOption) => (
                                <Chip
                                    key={roleOption}
                                    value={t(`members.roles.${roleOption}`)}
                                    isSelected={role === roleOption}
                                    onClick={() =>
                                        setValue('role', roleOption, {
                                            shouldValidate: true,
                                        })
                                    }
                                />
                            ),
                        )}
                    </div>
                </div>

                {canManageOrgRoles && (
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm-medium text-white-100">
                            {t('members.additionalRoles')}
                        </label>
                        <div className="flex gap-2">
                            <Chip
                                value={t('members.owner')}
                                isSelected={isOrgOwner}
                                variant="positive"
                                onClick={() =>
                                    setValue('isOrgOwner', !isOrgOwner, {
                                        shouldValidate: true,
                                    })
                                }
                            />
                            <Chip
                                value={t('members.userManager')}
                                isSelected={isOrgUserManager}
                                variant="positive"
                                onClick={() =>
                                    setValue(
                                        'isOrgUserManager',
                                        !isOrgUserManager,
                                        { shouldValidate: true },
                                    )
                                }
                            />
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-4">
                    <TextField
                        label={t('members.password')}
                        labelClassName="text-sm-medium text-white-100"
                        type="password"
                        required
                        {...register('password', {
                            validate: (value) => value.length > 0,
                        })}
                    />

                    <TextField
                        label={t('members.confirmPassword')}
                        labelClassName="text-sm-medium text-white-100"
                        type="password"
                        required
                        error={
                            errors.confirmPassword
                                ? t('members.passwordsMismatch')
                                : undefined
                        }
                        {...register('confirmPassword', {
                            validate: (value) => value === password,
                        })}
                    />

                    {policy && (
                        <PasswordRequirements
                            password={password}
                            policy={policy}
                        />
                    )}

                    <p className="text-sm text-grey-200">
                        {t('members.passwordChangeRequiredNotice')}
                    </p>
                </div>
            </div>
        </Modal>
    );
}
